import moment from 'moment';
import upperFirst from 'lodash/upperFirst';
import startCase from 'lodash/startCase';
import { ProductMap } from '@/utils/productMap';
import AVAnalyticsEventMap from '@/utils/AVAnalyticsEventMap';
import { AVRProducts, AVAnalyticsEventNames, AVAnalyticsEventTriggers } from '@/utils/enums';

/**
 * Get the form type needed by ASC by product
 * @param product
 * @returns {string}
 */
const getASCFormTypeByProduct = product => {
  switch (product) {
    case AVRProducts.CREDIT:
      return 'finance/credit';
    case AVRProducts.TRADE:
      return 'trade';
    case AVRProducts.TEST_DRIVE:
      return 'test_drive';
    default:
      return 'unknown';
  }
};

const birthdateShaper = originalBirthdate => {
  if (typeof originalBirthdate !== 'string' && !(originalBirthdate instanceof String)) {
    return '';
  }

  const parsedDate = moment(originalBirthdate, 'YYYY/MM/DD', true);

  if (!parsedDate.isValid()) {
    return '';
  }

  return parsedDate.format('YYYY-MM-DD');
};

const phoneNumberShaper = phone => {
  const cleaned = ` ${phone}`.replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? '+1 ' : '';
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return null;
};

const postalCodeShaper = postalCode => {
  if (typeof postalCode === 'string' && postalCode.length === 6) {
    return `${postalCode.slice(0, 3).toUpperCase()} ${postalCode.slice(3).toUpperCase()}`;
  }
  return null;
};

// We need this function because our endpoint returns Grades as numbers. We need them as letters.
const creditGradeBiMap = creditScore => {
  const correspondingCreditGradeFor = {
    A: 5,
    B: 4,
    C: 3,
    D: 2,
    E: 1,
    5: 'A',
    4: 'B',
    3: 'C',
    2: 'D',
    1: 'E',
  };
  return correspondingCreditGradeFor[creditScore] || null; // Backend is nullable now.
};

const creditRatingTypeMap = bureauCode => {
  // As of now, the bureauCodes map directly to a single ratingType as we don't support multiple
  // ratingTypes for a single bureau. If we add multiple ratingTypes for a single bureau we should
  // either expand this list and logic or create a second field to denote the ratingType per bureau.
  const ratingTypeMap = {
    TU: 'transunion-personalized',
    EFX: 'equifax-regional',
    XPN: 'experian-personalized',
    EFX_PERSONALIZED: 'equifax-personalized',
    EFX_ID_AUTH_NEIGHBOURHOOD_CREDIT: 'equifax-id-auth-neighbourhood',
  };
  return ratingTypeMap[bureauCode] || null;
};

const internationalizationShaper = languageCode => languageCode.replace('_', '-');

const parseAndCleanData = data => {
  let cleanedData;
  try {
    cleanedData = JSON.parse(data);
  } catch (e) {
    cleanedData = data;
  }
  if (typeof cleanedData === 'object' && cleanedData !== null) {
    const itemsToRemove = ['answer_0', 'answer_1', 'answer_2'];
    itemsToRemove.forEach(item => {
      if (cleanedData[`${item}`]) {
        cleanedData[`${item}`] = '--REDACTED--';
      }
    });
  }
  return cleanedData;
};

/**
 *
 * @param to
 * @param from
 * @returns {boolean}
 */
const preventRouteChange = (to, from) => {
  // Can't go back to avr from ecomV2 - temp code to prevent lead issues - https://mobials.atlassian.net/browse/EC-1435
  // If your lead is expired you can't do anything else
  if (
    (from.path.includes('e-commerce-v2') && !to.path.includes('e-commerce-v2')) ||
    from.path.includes('lead-expired')
  ) {
    return true;
  }
  return false;
};

/**
 *
 * @param path {string} route path
 * @returns {string|null} product name
 */
const getProductFromRoute = path => {
  const pathsArray = ['/mobile/', '/desktop/', '/embed/'];
  const filteredPathsArray = pathsArray.filter(item => path.includes(item));
  const filteredPath = filteredPathsArray[0] ? filteredPathsArray[0] : null;
  const filteredProductArray = ProductMap.filter(item => path.includes(`${filteredPath}${item.route}`));
  return filteredProductArray[0] ? filteredProductArray[0].name : null;
};

/**
 *
 * @param path
 * @param eventTrigger
 * @param store
 */
const sendAVAnalyticsEvent = (path, eventTrigger, store) => {
  // Look for an event with the correct route, trigger, and product
  let eventName;
  let event = {};
  const currentProduct = getProductFromRoute(path);
  const events = AVAnalyticsEventMap.filter(item => !item.product || (item.product && currentProduct === item.product));
  if (eventTrigger === AVAnalyticsEventTriggers.PAGE_VIEW) {
    // form open event, page views on start pages
    event = events.find(item => item.startPages && item.startPages.some(page => path.includes(page)));
    eventName = event ? AVAnalyticsEventNames.FORM_OPEN : null;
    // form complete event, page views on complete pages
    if (!eventName) {
      event = events.find(item => item.completePages && item.completePages.some(page => path.includes(page)));
      eventName = event ? AVAnalyticsEventNames.FORM_COMPLETE : null;
    }
  } else if (eventTrigger === AVAnalyticsEventTriggers.INTERACTION) {
    event = events.find(item => item.startPages && item.startPages.some(page => path.includes(page)));
    eventName = event ? AVAnalyticsEventNames.FORM_START : null;
  } else if (eventTrigger === AVAnalyticsEventTriggers.FORM_SUBMIT) {
    event = events.find(item => item.submitPages && item.submitPages.some(page => path.includes(page)));
    eventName = event ? AVAnalyticsEventNames.FORM_COMPLETE : null;
  }
  if (event && eventName) {
    const eventData = {
      name: eventName,
      flowName: event.flowName,
      formName: event.formName,
      route: path,
      isCOAGOnlyEvent: event.isCOAGOnlyEvent,
    };
    const duplicateEvent = store.state.analytics.AVAnalyticsEventsFired.find(
      item => eventName === item.name && event.formName === item.formName
    );
    // Make sure it hasn't already fired in this session
    if (!duplicateEvent) {
      store.commit('analytics/AV_ANALYTICS_EVENT', eventData);
    }
  }
};

/**
 * Blocks user from going to any other pages
 * @param to
 * @param from
 * @param next
 */
const keepInRoute = (to, from, next) => next(false);

/**
 * Blocks a route from going to a list of specific routes
 * @param namedRouteList {string[]}
 */
const ignoreRoutes = namedRouteList => (to, from, next) => {
  // Use with beforeRouteLeave in-component router guard from the component you dont want to navigate away from
  if (Array.isArray(namedRouteList) && namedRouteList.some(name => to.name?.includes(name) || to.path.includes(name))) {
    next(false);
  } else {
    next();
  }
};

/**
 * Only allows a list of specific routes to enter a route
 * @param namedRouteList {string[]}
 */
const onlyAcceptRoutes = namedRouteList => (to, from, next) => {
  // Use with beforeRouteEnter in-component router guard from the component that will allow the list of routes
  const routeListIsArray = Array.isArray(namedRouteList);
  const routeListIsEmpty = routeListIsArray && namedRouteList.length === 0;
  if (
    !routeListIsArray ||
    routeListIsEmpty ||
    (routeListIsArray && namedRouteList.some(name => from.name?.includes(name) || from.path.includes(name)))
  ) {
    next();
  } else {
    next(false);
  }
};

/**
 *
 * @param vehicleData
 * @param vehicleAttributes
 * @returns {string}
 */
const getVehicleName = (vehicleData, vehicleAttributes = ['year', 'make', 'model', 'trim']) => {
  let vehicleName = '';
  vehicleAttributes.forEach((item, index) => {
    if (vehicleData[`${item}`]) {
      vehicleName += vehicleData[`${item}`];
      if (index < vehicleAttributes.length - 1) {
        vehicleName += ' ';
      }
    }
  });
  return vehicleName;
};

/**
 *
 * @param path
 * @returns {boolean}
 */
const isAVFinanceFlow = path => path.includes('/finance-application/');

/**
 *
 * @param postMessageData {object}
 * @returns {boolean}
 */
const isStructuredPostMessageFromSDK = postMessageData => {
  const isObject = typeof postMessageData === 'object';
  const eventTypeIsSet = isObject && 'eventType' in postMessageData;
  const eventValueIsSet = isObject && 'eventValue' in postMessageData;
  const sourceIsSDK = isObject && 'eventSource' in postMessageData && postMessageData.eventSource === 'autoverify_sdk';

  return eventTypeIsSet && eventValueIsSet && sourceIsSDK;
};

const generateShiftDigitalPayload = (product, event, storeRootState) => {
  if (!product && !event && !storeRootState) {
    return {};
  }

  const formVehicle = {
    status: storeRootState.vehicle.vehicleInfo ? storeRootState.vehicle.vehicleInfo.status : '',
    year: storeRootState.vehicle.vehicleInfo ? storeRootState.vehicle.vehicleInfo.year : '',
    make: storeRootState.vehicle.vehicleInfo ? storeRootState.vehicle.vehicleInfo.make : '',
    model: storeRootState.vehicle.vehicleInfo ? storeRootState.vehicle.vehicleInfo.model : '',
    trim: storeRootState.vehicle.vehicleInfo ? storeRootState.vehicle.vehicleInfo.trim : '',
    vin: storeRootState.vehicle.vehicleInfo ? storeRootState.vehicle.vehicleInfo.vin : '',
    displayedPrice: storeRootState.vehicle.vehicleInfo ? storeRootState.vehicle.vehicleInfo.price : '',
  };

  if (product === AVRProducts.TRADE && event === AVAnalyticsEventNames.FORM_START) {
    return {
      tradeInSessionId: '1',
      tradeInProvider: storeRootState.business ? storeRootState.business.info.tradeInSource : '',
      events: 'tradeInStart',
    };
  }
  if (product === AVRProducts.TRADE && event === AVAnalyticsEventNames.FORM_COMPLETE) {
    return {
      tradeInSessionId: '1',
      tradeInProvider: storeRootState.business ? storeRootState.business.info.tradeInSource : '',
      tradeInYear: storeRootState.trade ? storeRootState.trade.vehicle.year : '',
      tradeInMake: storeRootState.trade ? storeRootState.trade.vehicle.make : '',
      tradeInModel: storeRootState.trade ? storeRootState.trade.vehicle.model : '',
      tradeInLeadId: storeRootState.lead ? storeRootState.lead.leadId : '',
      events: 'tradeInComplete',
    };
  }

  if (product === AVRProducts.CREDIT && event === AVAnalyticsEventNames.FORM_START) {
    return {
      formType: 'Other - credit prequalify',
      displayType: 'modal',
      formVehicle,
      events: 'formInitiation',
    };
  }
  if (product === AVRProducts.CREDIT && event === AVAnalyticsEventNames.FORM_COMPLETE) {
    return {
      formType: 'Other - credit prequalify',
      displayType: 'modal',
      leadId: storeRootState.lead ? storeRootState.lead.leadId : '',
      leadType: 'lead',
      prefContact: 'other',
      formVehicle,
      events: 'formSubmission',
    };
  }

  return {};
};

const creditVisionScoreImageName = creditScore => {
  const creditVisionScores = [
    {
      name: 'A-881-900',
      topScore: 900,
      bottomScore: 881,
    },
    {
      name: 'A-851-880',
      topScore: 880,
      bottomScore: 851,
    },
    {
      name: 'A-821-850',
      topScore: 850,
      bottomScore: 821,
    },
    {
      name: 'A-801-820',
      topScore: 820,
      bottomScore: 801,
    },
    {
      name: 'B-791-800',
      topScore: 800,
      bottomScore: 791,
    },
    {
      name: 'B-771-790',
      topScore: 790,
      bottomScore: 771,
    },
    {
      name: 'B-761-770',
      topScore: 770,
      bottomScore: 761,
    },
    {
      name: 'C-751-760',
      topScore: 760,
      bottomScore: 751,
    },
    {
      name: 'C-731-750',
      topScore: 750,
      bottomScore: 731,
    },
    {
      name: 'C-721-730',
      topScore: 730,
      bottomScore: 721,
    },
    {
      name: 'D-701-720',
      topScore: 720,
      bottomScore: 701,
    },
    {
      name: 'D-661-700',
      topScore: 700,
      bottomScore: 661,
    },
    {
      name: 'D-641-660',
      topScore: 660,
      bottomScore: 641,
    },
    {
      name: 'E-601-640',
      topScore: 640,
      bottomScore: 601,
    },
    {
      name: 'E-501-600',
      topScore: 600,
      bottomScore: 501,
    },
    {
      name: 'E-401-500',
      topScore: 500,
      bottomScore: 401,
    },
    {
      name: 'E-350-400',
      topScore: 400,
      bottomScore: 350,
    },
  ];
  const res = creditVisionScores
    ? creditVisionScores.filter(el => creditScore <= el.topScore && creditScore >= el.bottomScore)
    : null;
  return res.length === 1 ? res[0].name : null;
};

const getDisclaimerByProvince = provinceCode => {
  switch (provinceCode) {
    case 'ON':
      return 'message.credit.priceDisclaimer.ON';
    case 'AB':
      return 'message.credit.priceDisclaimer.AB';
    default:
      return 'message.credit.priceDisclaimer.default';
  }
};

const ecomFlowSelected = routePath => {
  switch (true) {
    case routePath.includes('/explore-calculator/'):
      return 'explore-calculator';
    case routePath.includes('/personalized-payment/'):
      return 'personalized-payment';
    default:
      return null;
  }
};

const currentSubFlow = routePath => {
  switch (true) {
    case routePath.includes('/trade-in/'):
      return 'trade-in';
    case routePath.includes('/credit/'):
      return 'credit';
    case routePath.includes('/finance/'):
      return 'finance';
    case routePath.includes('/offer/'):
      return 'offer';
    default:
      return null;
  }
};

const getASCSpecificProductName = (product, routePath) => {
  let ascProductName;
  let flowSelected;
  let subFlow;
  switch (product) {
    case AVRProducts.ECOMV2:
      flowSelected =
        typeof ecomFlowSelected(routePath) === 'string' ? ` - ${startCase(ecomFlowSelected(routePath))}` : '';
      subFlow = typeof currentSubFlow(routePath) === 'string' ? ` - ${startCase(currentSubFlow(routePath))}` : '';
      ascProductName = `Ecom V2${flowSelected}${subFlow}`;
      return ascProductName;
    default:
      ascProductName = undefined;
  }

  return ascProductName;
};

/*
 * Generates an ASC event payload
 * @param payload {object} - The payload object from the AV_ANALYTICS_EVENT mutation
 * @param rootState {object} - The root state of the store
 */
const generateASCEventPayload = (payload, vin) => {
  let eventName = '';
  let eventData = {};
  const { route, elementSubtype, eventActionResult } = payload;
  // Create the product name
  const product = getProductFromRoute(route);
  let productName = product ? product.replace(/([A-Z])/g, ' $1') : null;
  productName = productName ? upperFirst(productName) : null;
  productName =
    getASCSpecificProductName(product, route) !== undefined ? getASCSpecificProductName(product, route) : productName;
  // Create the form name from the product and the page route
  const pageRoute = route.split('/').pop();
  const pageRouteName = upperFirst(pageRoute.replace(/-/g, ' '));
  const formName = `${productName ? `${productName} - ` : ''}${pageRouteName}`;
  // Get the form type
  const formType = getASCFormTypeByProduct(product);
  if (payload.name === AVAnalyticsEventNames.CTA_INTERACTION) {
    eventName = 'asc_cta_interaction';
    eventData = {
      element_text: payload.elementText,
      element_type: 'digital_retailing_tool',
      element_subtype:
        typeof elementSubtype === 'string' && elementSubtype !== 'undefined' ? elementSubtype : 'cta_button',
      event_action: 'click',
      event_action_result:
        typeof eventActionResult === 'string' && eventActionResult !== 'undefined' ? eventActionResult : 'none',
      link_url: null,
      product_name: productName,
    };
  } else if (payload.name === AVAnalyticsEventNames.FORM_ENGAGEMENT) {
    eventName = 'asc_form_engagement';
    eventData = {
      comm_type: 'form',
      comm_status: null,
      element_text: payload.elementText,
      department: 'unknown',
      form_name: formName,
      form_type: formType,
      product_name: productName,
    };
  } else if (payload.name === AVAnalyticsEventNames.LEAD_COMPLETE) {
    eventName = 'asc_form_submission';
    eventData = {
      comm_type: 'form',
      comm_outcome: 'crm_update',
      submission_id: payload.leadId,
      element_text: null,
      department: 'unknown',
      form_name: formName,
      form_type: formType,
      product_name: productName,
    };
  } else if (
    payload.name === AVAnalyticsEventNames.FORM_OPEN ||
    payload.name === AVAnalyticsEventNames.FORM_START ||
    payload.name === AVAnalyticsEventNames.FORM_COMPLETE
  ) {
    eventName = 'asc_retail_process';
    let flowOutcome;
    if (payload.name === AVAnalyticsEventNames.FORM_OPEN) {
      flowOutcome = 'open';
    } else if (payload.name === AVAnalyticsEventNames.FORM_START) {
      flowOutcome = 'start';
    } else if (payload.name === AVAnalyticsEventNames.FORM_COMPLETE) {
      flowOutcome = 'finished';
    }
    eventData = {
      flow_name: payload.flowName,
      flow_outcome: flowOutcome,
      element_title: null,
      product_name: productName,
    };
  }
  if (eventName && eventData) {
    return {
      event_type: 'asc',
      event_name: eventName,
      vin,
      event_data: eventData,
    };
  }
  return null;
};

export default {
  birthdateShaper,
  phoneNumberShaper,
  creditGradeBiMap,
  internationalizationShaper,
  postalCodeShaper,
  parseAndCleanData,
  preventRouteChange,
  getProductFromRoute,
  creditRatingTypeMap,
  sendAVAnalyticsEvent,
  keepInRoute,
  ignoreRoutes,
  onlyAcceptRoutes,
  getVehicleName,
  isAVFinanceFlow,
  isStructuredPostMessageFromSDK,
  generateShiftDigitalPayload,
  getDisclaimerByProvince,
  creditVisionScoreImageName,
  generateASCEventPayload,
  getASCFormTypeByProduct,
  ecomFlowSelected,
  currentSubFlow,
  getASCSpecificProductName,
};
